import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { UploadZone } from '../sidebar/UploadZone';
import { SettingsPanel } from '../sidebar/SettingsPanel';
import { FileList } from '../sidebar/FileList';
import { ActionButtons } from '../sidebar/ActionButtons';
import { UploadedFile } from '../../types/detection';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  // Upload zone props
  isDragging: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onBrowseClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  allowedFormats: string[];
  // Settings props
  confThreshold: number;
  onConfThresholdChange: (value: number) => void;
  pixelToUm: number;
  onPixelToUmChange: (value: number) => void;
  // File list props
  files: UploadedFile[];
  onRemoveFile: (id: string) => void;
  onClearAll: () => void;
  onFileClick: (id: string) => void;
  activeFileId?: string;
  formatFileSize: (bytes: number) => string;
  // Action button props
  isProcessing: boolean;
  processingProgress: number;
  pendingCount: number;
  hasResults: boolean;
  onProcess: () => void;
  onCancel: () => void;
  onExport: () => void;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  // Upload zone props
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onBrowseClick,
  fileInputRef,
  onFileInputChange,
  allowedFormats,
  // Settings props
  confThreshold,
  onConfThresholdChange,
  pixelToUm,
  onPixelToUmChange,
  // File list props
  files,
  onRemoveFile,
  onClearAll,
  onFileClick,
  activeFileId,
  formatFileSize,
  // Action button props
  isProcessing,
  processingProgress,
  pendingCount,
  hasResults,
  onProcess,
  onCancel,
  onExport,
}) => {
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle file click - scroll to result and close drawer
  const handleFileClick = (id: string) => {
    onFileClick(id);
    onClose();
  };

  // Handle process - close drawer after starting
  const handleProcess = () => {
    onProcess();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className={cn(
          "fixed top-0 left-0 bottom-0 w-[300px] bg-white dark:bg-gray-900 shadow-xl z-50",
          "transform transition-transform duration-300 ease-in-out md:hidden",
          "flex flex-col",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
          <h2 className="font-semibold text-lg">Upload & Settings</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <SettingsPanel
            confThreshold={confThreshold}
            onConfThresholdChange={onConfThresholdChange}
            pixelToUm={pixelToUm}
            onPixelToUmChange={onPixelToUmChange}
          />

          <UploadZone
            isDragging={isDragging}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onBrowseClick={onBrowseClick}
            fileInputRef={fileInputRef}
            onFileInputChange={onFileInputChange}
            allowedFormats={allowedFormats}
          />

          <FileList
            files={files}
            onRemoveFile={onRemoveFile}
            onClearAll={onClearAll}
            onFileClick={handleFileClick}
            activeFileId={activeFileId}
            isProcessing={isProcessing}
            formatFileSize={formatFileSize}
          />
        </div>

        {/* Sticky action buttons */}
        <ActionButtons
          isProcessing={isProcessing}
          processingProgress={processingProgress}
          pendingCount={pendingCount}
          hasResults={hasResults}
          onProcess={handleProcess}
          onCancel={onCancel}
          onExport={onExport}
        />
      </aside>
    </>
  );
};
